import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Container, Button, ButtonGroup, Alert } from '@openedx/paragon';
import { useIntl } from '@openedx/frontend-base';
import { useAlert } from '@src/providers/AlertProvider';
import { useCourseInfo } from '@src/data/apiHook';
import { PendingTasks } from '@src/components/PendingTasks';
import CertificatesPageHeader from '@src/certificates/components/CertificatesPageHeader';
import IssuedCertificatesTab from '@src/certificates/components/IssuedCertificatesTab';
import GenerationHistoryTable from '@src/certificates/components/GenerationHistoryTable';
import GrantExceptionsModal from '@src/certificates/components/GrantExceptionsModal';
import InvalidateCertificateModal from '@src/certificates/components/InvalidateCertificateModal';
import RemoveExceptionModal from '@src/certificates/components/RemoveExceptionModal';
import RemoveInvalidationModal from '@src/certificates/components/RemoveInvalidationModal';
import DisableCertificatesModal from '@src/certificates/components/DisableCertificatesModal';
import RegenerateCertificatesModal from '@src/certificates/components/RegenerateCertificatesModal';
import GenerateCertificatesModal from '@src/certificates/components/GenerateCertificatesModal';
import {
  useCertificateGenerationHistory,
  useGrantBulkExceptions,
  useInvalidateCertificate,
  useIssuedCertificates,
  useRegenerateCertificates,
  useRemoveException,
  useRemoveInvalidation,
  useToggleCertificateGeneration,
  useUploadBulkExceptionsCsv,
} from '@src/certificates/data/apiHook';
import { CertificateFilter } from '@src/certificates/types';
import { CERTIFICATES_PAGE_SIZE, TAB_KEYS, MODAL_TITLES, ALERT_VARIANTS } from '@src/certificates/constants';
import { getErrorMessage } from '@src/certificates/utils/errorHandling';
import messages from '@src/certificates/messages';
import './CertificatesPage.scss';

const CertificatesPage = () => {
  const intl = useIntl();
  const { courseId = '' } = useParams<{ courseId: string }>();
  const { showToast, showModal } = useAlert();
  const { data: courseInfo } = useCourseInfo(courseId);

  const [filter, setFilter] = useState<CertificateFilter>(CertificateFilter.ALL_LEARNERS);
  const [search, setSearch] = useState('');
  const [certificatesPage, setCertificatesPage] = useState(0);
  const [tasksPage, setTasksPage] = useState(0);
  const [activeTab, setActiveTab] = useState<typeof TAB_KEYS.ISSUED | typeof TAB_KEYS.HISTORY>(TAB_KEYS.ISSUED);
  const [selectedUsername, setSelectedUsername] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [isCertificateGenerationEnabled, setIsCertificateGenerationEnabled] = useState(true);

  const [isGrantExceptionsOpen, setIsGrantExceptionsOpen] = useState(false);
  const [isInvalidateCertificateOpen, setIsInvalidateCertificateOpen] = useState(false);
  const [isRemoveExceptionOpen, setIsRemoveExceptionOpen] = useState(false);
  const [isRemoveInvalidationOpen, setIsRemoveInvalidationOpen] = useState(false);
  const [isDisableCertificatesOpen, setIsDisableCertificatesOpen] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isPendingTasksOpen, setIsPendingTasksOpen] = useState(false);

  const {
    data: certificatesData,
    isLoading: isLoadingCertificates,
  } = useIssuedCertificates(courseId, {
    page: certificatesPage,
    pageSize: CERTIFICATES_PAGE_SIZE,
    filter,
    search,
  });

  const {
    data: historyData,
    isLoading: isLoadingHistory,
  } = useCertificateGenerationHistory(courseId, {
    page: tasksPage,
    pageSize: CERTIFICATES_PAGE_SIZE,
  });

  const { mutate: grantExceptions, isPending: isGrantingExceptions } = useGrantBulkExceptions(courseId);
  const { mutate: uploadCsvExceptions, isPending: isUploadingCsv } = useUploadBulkExceptionsCsv(courseId);
  const { mutate: invalidateCert, isPending: isInvalidating } = useInvalidateCertificate(courseId);
  const { mutate: removeExcept, isPending: isRemovingException } = useRemoveException(courseId);
  const { mutate: removeInval, isPending: isRemovingInvalidation } = useRemoveInvalidation(courseId);
  const { mutate: toggleGeneration, isPending: isTogglingGeneration } = useToggleCertificateGeneration(courseId);
  const { mutate: regenerateCerts } = useRegenerateCertificates(courseId);

  const handleGrantExceptions = useCallback((learners: string[], notes: string) => {
    grantExceptions(
      { learners, notes },
      {
        onSuccess: (data) => {
          setIsGrantExceptionsOpen(false);
          if (data.errors && data.errors.length > 0) {
            const errorMessages = data.errors.map(err => `${err.learner}: ${err.message}`).join('\n');
            showModal({
              title: intl.formatMessage(messages.errorModalTitle),
              message: `Some exceptions failed:\n${errorMessages}`,
              variant: ALERT_VARIANTS.WARNING,
            });
          }
          if (data.success && data.success.length > 0) {
            showToast(intl.formatMessage(messages.exceptionsGrantedToast, { count: data.success.length }));
          }
        },
        onError: (error) => {
          showModal({
            title: intl.formatMessage(messages.errorModalTitle),
            message: getErrorMessage(error, intl.formatMessage(messages.errorGrantException)),
            variant: ALERT_VARIANTS.DANGER,
          });
        },
      },
    );
  }, [grantExceptions, showToast, showModal, intl]);

  const handleUploadCsvExceptions = useCallback((file: File) => {
    uploadCsvExceptions(
      file,
      {
        onSuccess: (data) => {
          setIsGrantExceptionsOpen(false);
          if (data.errors && data.errors.length > 0) {
            const errorMessages = data.errors.map(err => `${err.learner}: ${err.message}`).join('\n');
            showModal({
              title: intl.formatMessage(messages.errorModalTitle),
              message: `Some exceptions failed:\n${errorMessages}`,
              variant: ALERT_VARIANTS.WARNING,
            });
          }
          if (data.success && data.success.length > 0) {
            showToast(intl.formatMessage(messages.exceptionsGrantedToast, { count: data.success.length }));
          }
        },
        onError: (error) => {
          showModal({
            title: intl.formatMessage(messages.errorModalTitle),
            message: getErrorMessage(error, intl.formatMessage(messages.errorGrantException)),
            variant: ALERT_VARIANTS.DANGER,
          });
        },
      },
    );
  }, [uploadCsvExceptions, showToast, showModal, intl]);

  const handleInvalidateCertificate = useCallback((learners: string[], notes: string) => {
    invalidateCert(
      { learners, notes },
      {
        onSuccess: (data) => {
          setIsInvalidateCertificateOpen(false);
          if (data.errors && data.errors.length > 0) {
            const errorMessages = data.errors.map(err => `${err.learner}: ${err.message}`).join('\n');
            showModal({
              title: intl.formatMessage(messages.errorModalTitle),
              message: `Some invalidations failed:\n${errorMessages}`,
              variant: ALERT_VARIANTS.WARNING,
              confirmText: intl.formatMessage(messages.close),
            });
          }
          if (data.success && data.success.length > 0) {
            showToast(intl.formatMessage(messages.certificatesInvalidatedToast, { count: data.success.length }));
          }
        },
        onError: (error) => {
          showModal({
            title: intl.formatMessage(messages.errorModalTitle),
            message: getErrorMessage(error, intl.formatMessage(messages.errorInvalidateCertificate)),
            variant: ALERT_VARIANTS.DANGER,
          });
        },
      },
    );
  }, [invalidateCert, showToast, showModal, intl]);

  const handleRemoveExceptionClick = useCallback((username: string, email: string) => {
    setSelectedUsername(username);
    setSelectedEmail(email);
    setIsRemoveExceptionOpen(true);
  }, []);

  const handleRemoveExceptionConfirm = useCallback(() => {
    // Backend accepts either username or email - use whichever is available
    const identifier = selectedUsername || selectedEmail;

    if (!identifier) {
      showModal({
        title: MODAL_TITLES.ERROR,
        message: intl.formatMessage(messages.errorRemoveException) + ': Username or email is required',
        variant: ALERT_VARIANTS.DANGER,
      });
      return;
    }

    removeExcept(
      { username: identifier },
      {
        onSuccess: () => {
          setIsRemoveExceptionOpen(false);
          setSelectedUsername('');
          setSelectedEmail('');
          showToast(intl.formatMessage(messages.exceptionRemovedToast, { email: selectedEmail }));
        },
        onError: (error) => {
          showModal({
            title: intl.formatMessage(messages.errorModalTitle),
            message: getErrorMessage(error, intl.formatMessage(messages.errorRemoveException)),
            variant: ALERT_VARIANTS.DANGER,
          });
        },
      },
    );
  }, [removeExcept, selectedUsername, selectedEmail, showToast, showModal, intl]);

  const handleRemoveInvalidationClick = useCallback((username: string, email: string) => {
    setSelectedUsername(username);
    setSelectedEmail(email);
    setIsRemoveInvalidationOpen(true);
  }, []);

  const handleRemoveInvalidationConfirm = useCallback(() => {
    // Backend accepts either username or email - use whichever is available
    const identifier = selectedUsername || selectedEmail;

    if (!identifier) {
      showModal({
        title: MODAL_TITLES.ERROR,
        message: intl.formatMessage(messages.errorRemoveInvalidation) + ': Username or email is required',
        variant: ALERT_VARIANTS.DANGER,
      });
      return;
    }

    removeInval(
      { username: identifier },
      {
        onSuccess: () => {
          setIsRemoveInvalidationOpen(false);
          setSelectedUsername('');
          setSelectedEmail('');
          showToast(intl.formatMessage(messages.invalidationRemovedToast, { email: selectedEmail }));
        },
        onError: (error) => {
          showModal({
            title: intl.formatMessage(messages.errorModalTitle),
            message: getErrorMessage(error, intl.formatMessage(messages.errorRemoveInvalidation)),
            variant: ALERT_VARIANTS.DANGER,
          });
        },
      },
    );
  }, [removeInval, selectedUsername, selectedEmail, showToast, showModal, intl]);

  const handleToggleCertificateGeneration = useCallback(() => {
    const newState = !isCertificateGenerationEnabled;
    toggleGeneration(newState, {
      onSuccess: () => {
        setIsCertificateGenerationEnabled(newState);
        setIsDisableCertificatesOpen(false);
        showToast(
          newState
            ? intl.formatMessage(messages.successEnableCertificates)
            : intl.formatMessage(messages.successDisableCertificates),
        );
      },
      onError: (error) => {
        showModal({
          title: MODAL_TITLES.ERROR,
          message: getErrorMessage(error, intl.formatMessage(messages.errorToggleCertificateGeneration)),
          variant: ALERT_VARIANTS.DANGER,
        });
      },
    });
  }, [isCertificateGenerationEnabled, toggleGeneration, showToast, showModal, intl]);

  const handleRegenerateCertificatesClick = useCallback(() => {
    // Don't open modal for disabled filters
    if (filter === CertificateFilter.ALL_LEARNERS || filter === CertificateFilter.INVALIDATED) {
      return;
    }

    // For granted exceptions, open the generate modal
    if (filter === CertificateFilter.GRANTED_EXCEPTIONS) {
      setIsGenerateModalOpen(true);
    } else {
      // For other filters, open the regenerate modal
      setIsRegenerateModalOpen(true);
    }
  }, [filter]);

  const handleRegenerateCertificatesConfirm = useCallback(() => {
    regenerateCerts({ filter, onlyWithoutCertificate: false }, {
      onSuccess: () => {
        setIsRegenerateModalOpen(false);
        showToast(intl.formatMessage(messages.certificatesRegeneratedToast));
      },
      onError: (error) => {
        setIsRegenerateModalOpen(false);
        const errorMessage = getErrorMessage(error, intl.formatMessage(messages.errorRegenerateCertificates));
        showModal({
          title: MODAL_TITLES.ERROR,
          message: errorMessage,
          variant: ALERT_VARIANTS.DANGER,
        });
      },
    });
  }, [regenerateCerts, filter, showToast, showModal, intl]);

  const handleGenerateCertificatesConfirm = useCallback((onlyWithoutCertificate: boolean) => {
    regenerateCerts({ filter, onlyWithoutCertificate }, {
      onSuccess: () => {
        setIsGenerateModalOpen(false);
        showToast(intl.formatMessage(messages.certificatesRegeneratedToast));
      },
      onError: (error) => {
        setIsGenerateModalOpen(false);
        const errorMessage = getErrorMessage(error, intl.formatMessage(messages.errorRegenerateCertificates));
        showModal({
          title: MODAL_TITLES.ERROR,
          message: errorMessage,
          variant: ALERT_VARIANTS.DANGER,
        });
      },
    });
  }, [regenerateCerts, filter, showToast, showModal, intl]);

  const handleTabKeyDown = useCallback((event: React.KeyboardEvent) => {
    const tabs = [TAB_KEYS.ISSUED, TAB_KEYS.HISTORY];
    const currentIndex = tabs.indexOf(activeTab);

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab);

    // Focus the new tab
    setTimeout(() => {
      document.getElementById(`certificates-tab-${nextTab}`)?.focus();
    }, 0);
  }, [activeTab]);

  // Check if certificate management is disabled
  if (courseInfo && !courseInfo.certificatesEnabled) {
    return (
      <Container className="mt-4.5 mb-4" fluid>
        <Alert variant="warning">
          {intl.formatMessage(messages.certificatesDisabledMessage)}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4.5 mb-4" fluid>
      <CertificatesPageHeader
        onGrantExceptions={() => setIsGrantExceptionsOpen(true)}
        onInvalidateCertificate={() => setIsInvalidateCertificateOpen(true)}
        onStudentGeneratedCertificates={() => setIsDisableCertificatesOpen(true)}
      />

      <Card variant="muted" className="pt-3 pt-md-4 pb-4 pb-md-6 certificates-card">
        <ButtonGroup className="d-block mx-4" role="tablist">
          <Button
            id="certificates-tab-issued"
            role="tab"
            aria-controls="certificates-tabpanel-issued"
            aria-selected={activeTab === TAB_KEYS.ISSUED}
            tabIndex={activeTab === TAB_KEYS.ISSUED ? 0 : -1}
            onClick={() => setActiveTab(TAB_KEYS.ISSUED)}
            onKeyDown={handleTabKeyDown}
            variant={activeTab === TAB_KEYS.ISSUED ? 'primary' : 'outline-primary'}
          >
            {intl.formatMessage(messages.issuedCertificatesTab)}
          </Button>
          <Button
            id="certificates-tab-history"
            role="tab"
            aria-controls="certificates-tabpanel-history"
            aria-selected={activeTab === TAB_KEYS.HISTORY}
            tabIndex={activeTab === TAB_KEYS.HISTORY ? 0 : -1}
            onClick={() => setActiveTab(TAB_KEYS.HISTORY)}
            onKeyDown={handleTabKeyDown}
            variant={activeTab === TAB_KEYS.HISTORY ? 'primary' : 'outline-primary'}
          >
            {intl.formatMessage(messages.generationHistoryTab)}
          </Button>
        </ButtonGroup>
        <div
          id="certificates-tabpanel-issued"
          role="tabpanel"
          aria-labelledby="certificates-tab-issued"
          hidden={activeTab !== TAB_KEYS.ISSUED}
        >
          {activeTab === TAB_KEYS.ISSUED && (
            <IssuedCertificatesTab
              data={certificatesData?.results || []}
              isLoading={isLoadingCertificates}
              itemCount={certificatesData?.count || 0}
              pageCount={certificatesData?.numPages || 0}
              search={search}
              onSearchChange={setSearch}
              filter={filter}
              onFilterChange={setFilter}
              currentPage={certificatesPage}
              onPageChange={setCertificatesPage}
              onRemoveException={handleRemoveExceptionClick}
              onRemoveInvalidation={handleRemoveInvalidationClick}
              onRegenerateCertificates={handleRegenerateCertificatesClick}
            />
          )}
        </div>
        <div
          id="certificates-tabpanel-history"
          role="tabpanel"
          aria-labelledby="certificates-tab-history"
          hidden={activeTab !== TAB_KEYS.HISTORY}
        >
          {activeTab === TAB_KEYS.HISTORY && (
            <div className="d-flex flex-column mt-3 mt-md-4">
              <GenerationHistoryTable
                data={historyData?.results || []}
                isLoading={isLoadingHistory}
                itemCount={historyData?.count || 0}
                pageCount={historyData?.numPages || 0}
                currentPage={tasksPage}
                onPageChange={setTasksPage}
              />
            </div>
          )}
        </div>
      </Card>

      <GrantExceptionsModal
        isOpen={isGrantExceptionsOpen}
        onClose={() => setIsGrantExceptionsOpen(false)}
        onSubmit={handleGrantExceptions}
        onUploadCsv={handleUploadCsvExceptions}
        isSubmitting={isGrantingExceptions || isUploadingCsv}
      />
      <InvalidateCertificateModal
        isOpen={isInvalidateCertificateOpen}
        onClose={() => setIsInvalidateCertificateOpen(false)}
        onSubmit={handleInvalidateCertificate}
        isSubmitting={isInvalidating}
      />
      <RemoveExceptionModal
        isOpen={isRemoveExceptionOpen}
        email={selectedEmail}
        onClose={() => {
          setIsRemoveExceptionOpen(false);
          setSelectedUsername('');
          setSelectedEmail('');
        }}
        onConfirm={handleRemoveExceptionConfirm}
        isSubmitting={isRemovingException}
      />
      <RemoveInvalidationModal
        isOpen={isRemoveInvalidationOpen}
        email={selectedEmail}
        onClose={() => {
          setIsRemoveInvalidationOpen(false);
          setSelectedUsername('');
          setSelectedEmail('');
        }}
        onConfirm={handleRemoveInvalidationConfirm}
        isSubmitting={isRemovingInvalidation}
      />
      <DisableCertificatesModal
        isOpen={isDisableCertificatesOpen}
        isEnabled={isCertificateGenerationEnabled}
        onClose={() => setIsDisableCertificatesOpen(false)}
        onConfirm={handleToggleCertificateGeneration}
        isSubmitting={isTogglingGeneration}
      />
      <RegenerateCertificatesModal
        isOpen={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        onConfirm={handleRegenerateCertificatesConfirm}
        isSubmitting={false}
        filter={filter}
        learnerCount={certificatesData?.count || 0}
      />
      <GenerateCertificatesModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onConfirm={handleGenerateCertificatesConfirm}
        isSubmitting={false}
        learnerCount={certificatesData?.count || 0}
      />
      <PendingTasks isOpen={isPendingTasksOpen} onToggle={() => setIsPendingTasksOpen(prev => !prev)} />
    </Container>
  );
};

export default CertificatesPage;
